export type GeoQuestionCheckId =
  | 'title'
  | 'context'
  | 'symptom'
  | 'reproduction'
  | 'expected'
  | 'tags'
  | 'technical_detail'
  | 'no_fake_source';

export interface GeoQuestionCheck {
  id: GeoQuestionCheckId;
  label: string;
  hint: string;
  passed: boolean;
}

export interface GeoQuestionAnalysis {
  score: number;
  checks: GeoQuestionCheck[];
  missingHints: string[];
}

const codeBlockPattern = /```[\s\S]*?```|~~~[\s\S]*?~~~/;
const inlineCodePattern = /`[^`]+`/;
const commandPattern =
  /\b(go test|go build|npm|pnpm|yarn|docker|kubectl|curl|mysql|redis-cli|mvn|gradle|java|python|node)\b/i;
const contextPattern =
  /(环境|版本|操作系统|系统|浏览器|数据库|依赖|框架|SDK|JDK|Node|Go|Java|Python|MySQL|Redis|Docker|Kubernetes|Linux|Windows|macOS|iOS|Android|Chrome|Firefox|Edge)/i;
const symptomPattern =
  /(错误|报错|异常|日志|现象|表现|失败|不生效|无法|卡住|超时|error|exception|failed|panic|traceback|timeout)/i;
const reproductionPattern =
  /(复现|重现|步骤|操作|执行|命令|最小示例|demo|example|case|如何触发)/i;
const expectedPattern =
  /(期望|预期|实际|应该|希望|结果|想要|目标|expected|actual)/i;
const vagueTitlePattern =
  /^(求助|帮忙|问题|报错|不行|急|看看|怎么回事|为什么|请教)$/i;
const placeholderSourcePattern =
  /(example\.com|example\.org|待补充|待替换|TODO|官方文档链接|真实 URL|真实URL)/i;

const GEO_QUESTION_TEMPLATE = `## 背景
说明你正在做什么、目标是什么，以及问题出现在哪个流程里。

## 环境
- 语言/框架：
- 依赖/版本：
- 操作系统/数据库/浏览器：

## 现象
贴出完整错误信息、日志片段或异常表现。

\`\`\`text
在这里粘贴关键日志、命令输出或错误堆栈
\`\`\`

## 复现步骤
1. 
2. 
3. 

## 已尝试
- 写出已经查过或尝试过的方法，以及结果。

## 期望结果
说明你希望得到什么结果，以及当前实际结果是什么。

## 相关链接
- 如涉及官方文档、Issue、Release、源码或规范，请粘贴真实链接。
`;

const trimContent = (value: string) => value.trim();

const titleLooksSpecific = (title: string) => {
  const current = trimContent(title);
  if (current.length < 10 || vagueTitlePattern.test(current)) {
    return false;
  }
  return (
    /[\u4e00-\u9fa5A-Za-z0-9]/.test(current) &&
    /(如何|怎么|为什么|是否|报错|失败|不生效|配置|版本|升级|部署|查询|连接|启动|编译|登录|注册|缓存|性能|错误|异常|Can|How|Why|Error|Failed)/i.test(
      current,
    )
  );
};

export const appendGeoQuestionTemplate = (content: string) => {
  const current = trimContent(content);
  if (!current) {
    return GEO_QUESTION_TEMPLATE;
  }
  return `${current}\n\n${GEO_QUESTION_TEMPLATE}`;
};

export const analyzeGeoQuestion = (
  title: string,
  content: string,
  tagCount: number,
): GeoQuestionAnalysis => {
  const trimmed = trimContent(content);
  const hasTechnicalDetail =
    codeBlockPattern.test(trimmed) ||
    inlineCodePattern.test(trimmed) ||
    commandPattern.test(trimmed);
  const hasPlaceholderSource = placeholderSourcePattern.test(trimmed);

  const checks: GeoQuestionCheck[] = [
    {
      id: 'title',
      label: '标题具体可检索',
      hint: '标题直接写技术对象、动作和症状，例如“MySQL 8 连接超时如何排查”。',
      passed: titleLooksSpecific(title),
    },
    {
      id: 'context',
      label: '环境和版本清晰',
      hint: '写明语言、框架、数据库、系统、依赖或版本。版本信息越清楚越容易被引用。',
      passed: contextPattern.test(trimmed),
    },
    {
      id: 'symptom',
      label: '错误现象完整',
      hint: '贴出完整报错、日志、截图文字或异常表现，不只写“不行”。',
      passed: symptomPattern.test(trimmed),
    },
    {
      id: 'reproduction',
      label: '可复现步骤',
      hint: '按 1、2、3 写出触发步骤、执行命令或最小示例。',
      passed: reproductionPattern.test(trimmed),
    },
    {
      id: 'expected',
      label: '期望和实际结果',
      hint: '说明希望得到什么、实际发生了什么，回答者才能判断差距。',
      passed: expectedPattern.test(trimmed),
    },
    {
      id: 'tags',
      label: '标签准确',
      hint: '至少选择 1 个准确标签，标签会进入 llms-full.txt 和 AI prompt 信号。',
      passed: tagCount > 0,
    },
    {
      id: 'technical_detail',
      label: '有可验证材料',
      hint: '提供命令、配置、代码块、日志或最小复现，让回答可以被验证。',
      passed: hasTechnicalDetail,
    },
    {
      id: 'no_fake_source',
      label: '没有占位来源',
      hint: '不要保留 example.com、TODO、待补充等占位链接；有来源就放真实链接。',
      passed: !hasPlaceholderSource,
    },
  ];

  const missingHints = checks
    .filter((check) => !check.passed)
    .map((check) => check.hint);
  const score = Math.round(
    (checks.filter((check) => check.passed).length / checks.length) * 100,
  );

  return {
    score,
    checks,
    missingHints,
  };
};
